import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import {defineConfig, createSystem, ChakraProvider, defaultBaseConfig, Box} from '@chakra-ui/react3'

import {animationStyles} from './animation-styles'
import {breakpoints} from './breakpoints'
import {globalCss} from './global-css'
import {keyframes} from './keyframes'
import {layerStyles} from './layer-styles'
import {recipes} from './recipes'
import {semanticTokens} from './semantic-tokens'
import {slotRecipes} from './slot-recipes'
import {textStyles} from './text-styles'
import {tokens} from './tokens'

const THEME_SCOPE = ':is(html, .chakra-theme)'
const themeScope = (theme) => `*${THEME_SCOPE}[data-leap-theme=${theme}]`
const THEME_SCOPE_SELECTOR = `${THEME_SCOPE}[data-leap-theme]`

const config = defineConfig({
    cssVarsPrefix: 'leap',
    cssVarsRoot: ':where(html, .chakra-theme)',
    disableLayers: true, // required to allow recipes to work while chakra2 is also used
    preflight: true,
    conditions: {
        icon: '& :where(svg), & :where(.icon)', // use .icon for non-svg icons
        // nearest scope wins by specificity, up to three nested; `html &` makes light the default
        // (not `:root &`: emotion rewrites a leading `:` to `&:root`)
        // data-leap-theme because Chakra v2 owns data-theme on html
        light: [
            'html &',
            `${themeScope('light')} &`,
            `${themeScope('dark')} ${themeScope('light')} &`,
            `${themeScope('light')} ${themeScope('dark')} ${themeScope('light')} &`
        ].join(', '),
        dark: [
            `${themeScope('dark')} &`,
            `${themeScope('light')} ${themeScope('dark')} &`,
            `${themeScope('dark')} ${themeScope('light')} ${themeScope('dark')} &`
        ].join(', '),
        hasLeftIcon: '&:has(> svg:first-child, > .icon:first-child)', // use .icon for non-svg icons
        hasRightIcon: '&:has(> svg:last-child, > .icon:last-child)', // use .icon for non-svg icons
        hasOnlyIcon: '&:has(> svg:only-child, > .icon:only-child)' // use .icon for non-svg icons
    },
    globalCss,
    theme: {
        breakpoints,
        keyframes,
        tokens,
        semanticTokens,
        recipes,
        slotRecipes,
        textStyles,
        layerStyles,
        animationStyles
    }
})
const system = createSystem(defaultBaseConfig, config)

// an override to the default config that only uses mobile breakpoints for use in
// small containers like drawer
const mobileOnlyConfig = defineConfig({
    ...config,
    preflight: false,
    globalCss: false,
    theme: {
        ...config.theme,
        breakpoints: {
            ...Object.fromEntries(
                Object.keys(config.theme.breakpoints).map((key) => [key, '99999em'])
            ),
            base: '0px',
            xxs: '375px'
        }
    }
})
const mobileOnlySystem = createSystem(defaultBaseConfig, mobileOnlyConfig)

/**
 * Reads the theme in effect for the element the returned ref is attached to, by walking up
 * to the nearest themed scope -- a `Chakra3Theme` wrapper, or `<html>` for the page's own
 * theme.
 *
 * Only for values that must cross out of CSS -- styling our own elements light/dark goes
 * through the `_light`/`_dark` conditions, which need no JavaScript.
 *
 * @returns {[('light'|'dark'), function(HTMLElement|null): void]} The active theme
 *   (`light` outside any themed scope) and the ref to put on an element inside the subtree.
 */
export const useLeapTheme = () => {
    const [element, setElement] = useState(null)
    const [theme, setTheme] = useState('light')

    useEffect(() => {
        if (!element) {
            setTheme('light')
            return
        }

        const readTheme = () => {
            const scope = element.closest(THEME_SCOPE_SELECTOR)
            setTheme(scope?.dataset.leapTheme === 'dark' ? 'dark' : 'light')
        }
        readTheme()

        const observer = new MutationObserver(readTheme)
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['data-leap-theme'],
            subtree: true
        })

        return () => observer.disconnect()
    }, [element])

    return [theme, setElement]
}

/**
 * Theme wrapper for Chakra v3 sections.
 *
 * Applies the theme through the `chakra-theme` class and `data-leap-theme`
 * attribute, allowing nested content to be rendered in either light or dark
 * mode.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Content to render inside the themed wrapper.
 * @param {'light'|'dark'} [props.theme='light'] - Theme variant applied to the wrapper.
 * @returns {JSX.Element} The themed wrapper element.
 */
export const Chakra3Theme = ({children, theme = 'light'}) => {
    return (
        <Box className="chakra-theme" data-leap-theme={theme} display="contents">
            {children}
        </Box>
    )
}

Chakra3Theme.propTypes = {
    children: PropTypes.node,
    theme: PropTypes.oneOf(['light', 'dark'])
}

/**
 * Publishes the page theme as `data-leap-theme` on `<html>`. Everything outside a
 * nested `Chakra3Theme` follows it, portals included.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {'light'|'dark'} [props.theme] - The page's `c_colorTheme`.
 * @returns {JSX.Element|null} The `Helmet` that sets the attribute.
 */
export const PageTheme = ({theme}) =>
    theme ? <Helmet htmlAttributes={{'data-leap-theme': theme}} /> : null

PageTheme.propTypes = {
    theme: PropTypes.oneOf(['light', 'dark'])
}

const Chakra3Provider = ({children}) => {
    return <ChakraProvider value={system}>{children}</ChakraProvider>
}

Chakra3Provider.propTypes = {
    children: PropTypes.node
}

export const Chakra3MobileOnlyProvider = ({children}) => {
    return <ChakraProvider value={mobileOnlySystem}>{children}</ChakraProvider>
}

Chakra3MobileOnlyProvider.propTypes = {
    children: PropTypes.node
}

export default Chakra3Provider
